/**
 * Camada da chuva por satélite no mapa: a hora do GSMaP casada com cada
 * quadro do loop (`tempo.ts`), pintada e reprojetada para a grade do GOES.
 *
 * - Download: as horas de TODO o loop (no máximo 96 em 48 h, ~18 KB cada)
 *   assim que a camada liga, 3 por vez, do quadro atual para trás; ficam na
 *   memória como bytes crus (262 KB cada, ~25 MB nas 48 h). Antes do primeiro
 *   play, só a hora na tela e as já baixadas — como o satélite.
 * - Desenho: como a previsão (`previsao/motor.ts`) — o VALOR é interpolado
 *   (bilinear com a máscara de sem dado) numa grade 2× mais fina, pintado
 *   pela paleta da chuva do "Próximos dias" (a mesma cor é a mesma chuva no
 *   observado e no previsto) e só então reprojetado pela tabela de vizinho
 *   mais próximo do radar — num Web Worker (`pintura.worker.ts`): na thread
 *   principal eram ~55 ms por hora. As últimas pinturas ficam guardadas
 *   (voltar uma hora não repinta), e a pintura da tela nunca é fechada.
 * - Uma hora vale para três imagens do satélite seguidas (de 10 em 10 min):
 *   só a troca de hora repinta.
 * - A reprodução espera a hora do próximo quadro PINTADA (`registerFrameGate`,
 *   que já pede a pintura adiantada) e a exportação, a do atual na tela
 *   (`registerFrameReadiness`, reavaliada a cada mudança); o que ainda não
 *   ficou pronto deixa a anterior na tela (o painel diz "carregando").
 */
import type OlMap from 'ol/Map';
import { abortError, isAbortError } from '../lib/fetchRetry';
import { subscribeSelector } from '../lib/store';
import { registerFrameGate } from '../loop/preloader';
import { registerFrameReadiness } from '../loop/render';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { FrameImageLayer, FrameSource } from '../map/FrameLayer';
import { lonLatToMap, mapToLonLat } from '../map/projection';
import { Z } from '../map/zIndex';
import { camadaDe } from '../previsao/camadas';
import { extensaoLonLat, valorNoPonto, type Grade } from '../previsao/interp';
import { calcularGeometria, modeloLonLat, type GeometriaRadar } from '../radar/geometria';
import { shownTimeOf, useTimeStore } from '../state/time';
import { carregarQuadro, GSMAP_ATRIBUICAO, tabelaDeValores, type IndiceGsmap, type QuadroGsmap } from './api';
import { useGsmapStore, type ChuvaNoQuadro } from './store';
import { pintarHora, type PedidoPintura, type RespostaPintura } from './pintura.worker';
import { quadroParaInstante } from './tempo';

/** Quanto mais fina é a grade pintada (antes da reprojeção): 0,05° ≈ 5,5 km. */
const FATOR = 2;
const PARALELOS = 3;
/** Pinturas guardadas (~4 MB cada na GPU): a da tela, a próxima pintada adiantada e as recentes. */
const MAX_PINTURAS = 6;
/** Quadros à frente cuja hora já sai pintada (uma hora vale para ~3 imagens). */
const ADIANTAR_QUADROS = 4;

/** A paleta da chuva do "Próximos dias" (mm/h, sem cor abaixo de 0,1). */
export const PALETA_GSMAP = camadaDe('chuva').paleta;

export const chaveDe = (q: Pick<QuadroGsmap, 'c' | 'p'>): string => `${q.c}-${q.p}`;

type Entrada =
  | { estado: 'carregando'; ctrl: AbortController }
  | { estado: 'ok'; bytes: Uint8Array }
  | { estado: 'falhou' };

/** Bytes crus → mm/h (NaN sem dado). */
export function decodificar(bytes: Uint8Array, tabela: Float32Array): Float32Array {
  const v = new Float32Array(bytes.length);
  for (let k = 0; k < bytes.length; k++) v[k] = tabela[bytes[k]];
  return v;
}

/** Pinta no worker; sem Worker (testes, navegador antigo) ou com ele quebrado, na thread principal. */
class Pintor {
  private worker: Worker | null = null;
  private semWorker = typeof Worker === 'undefined';
  private seq = 0;
  private readonly pendentes = new Map<number, { pedido: Omit<PedidoPintura, 'id'>; ok: (b: ImageBitmap) => void; erro: (e: unknown) => void }>();

  pintar(pedido: Omit<PedidoPintura, 'id'>): Promise<ImageBitmap> {
    const w = this.obter();
    if (!w) return naThreadPrincipal(pedido);
    const id = ++this.seq;
    return new Promise((ok, erro) => {
      this.pendentes.set(id, { pedido, ok, erro });
      // Os bytes vão copiados: o cache da camada continua com os seus.
      w.postMessage({ id, ...pedido } satisfies PedidoPintura);
    });
  }

  private obter(): Worker | null {
    if (this.worker || this.semWorker) return this.worker;
    try {
      const w = new Worker(new URL('./pintura.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (e: MessageEvent<RespostaPintura>) => {
        const r = e.data;
        const p = this.pendentes.get(r.id);
        if (!p) {
          if (r.ok) r.image.close();
          return;
        }
        this.pendentes.delete(r.id);
        if (r.ok) p.ok(r.image);
        else p.erro(new Error(r.error));
      };
      w.onerror = (e) => {
        // O worker não subiu (ex.: arquivo de um deploy antigo): o que estava na
        // fila e o resto pintam na thread principal — os bytes continuam bons.
        console.warn('[AmaView] Worker da chuva falhou; pintando na thread principal:', e.message);
        this.semWorker = true;
        this.worker?.terminate();
        this.worker = null;
        const fila = [...this.pendentes.values()];
        this.pendentes.clear();
        for (const p of fila) naThreadPrincipal(p.pedido).then(p.ok, p.erro);
      };
      this.worker = w;
    } catch {
      this.semWorker = true;
    }
    return this.worker;
  }

  /** Camada desligada ou desmontada: o worker (e a tabela de ~4 MB dele) sai; os pedidos em voo são descartados. */
  encerrar(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const p of this.pendentes.values()) p.erro(abortError());
    this.pendentes.clear();
  }
}

function naThreadPrincipal(p: Omit<PedidoPintura, 'id'>): Promise<ImageBitmap> {
  return createImageBitmap(new ImageData(pintarHora(p), p.repro.largura, p.repro.altura));
}

export class GsmapLayerManager {
  readonly layer: FrameImageLayer;
  private readonly source: FrameSource;
  private readonly cache = new Map<string, Entrada>();
  /** Horas já pintadas (as mais recentes por último). */
  private readonly pinturas = new Map<string, ImageBitmap>();
  /** Horas sendo pintadas agora (no máximo uma por hora). */
  private readonly pedidos = new Set<string>();
  /** Sobe ao esvaziar (grade nova, desligar): pintura pedida antes não é guardada. */
  private geracao = 0;
  private fila: QuadroGsmap[] = [];
  private emVoo = 0;
  private quadros: QuadroGsmap[] = [];
  private meios: number[] = [];
  private indice: IndiceGsmap | null = null;
  private tabela: Float32Array | null = null;
  private geo: { grade: Grade; geo: GeometriaRadar; chave: string } | null = null;
  private readonly pintor = new Pintor();
  /** Hora na tela (a que a fonte desenha: a pintura dela nunca é fechada). */
  private naTela: string | null = null;
  private esperas: (() => void)[] = [];
  private readonly paradas: (() => void)[] = [];
  private disposed = false;

  constructor(private readonly map: OlMap) {
    this.source = new FrameSource([0, 0, 1, 1], GSMAP_ATRIBUICAO);
    this.layer = new FrameImageLayer({ source: this.source, visible: false, className: 'ol-layer gsmap-layer' });
    this.layer.setZIndex(Z.GSMAP);
    this.layer.set('id', 'gsmap');
    map.addLayer(this.layer);

    this.paradas.push(
      useGsmapStore.subscribe((a, b) => {
        if (a.opacidade !== b.opacidade) this.layer.setOpacity(a.opacidade);
        if (a.visivel !== b.visivel || a.indice !== b.indice) this.replanejar();
      }),
      useTimeStore.subscribe((a, b) => {
        // Antes do primeiro play cada quadro novo na tela pede a sua hora (sob demanda).
        if (a.frames !== b.frames || (a.index !== b.index && !loopLoadAllowed())) this.replanejar();
        else if (shownTimeOf(a) !== shownTimeOf(b)) this.mostrar();
      }),
      subscribeSelector(useLoopStore, (s) => loopLoadAllowed(s), () => this.replanejar()),
      registerFrameGate((i) => this.pronto(useTimeStore.getState().frames[i] ?? null)),
      registerFrameReadiness(() => this.quandoProntoAtual()),
    );
    this.layer.setOpacity(useGsmapStore.getState().opacidade);
    this.replanejar();
  }

  private quadroDe(t: number | null): QuadroGsmap | null {
    return quadroParaInstante(this.quadros, this.meios, t);
  }

  /**
   * A hora do instante `t` já pode ser desenhada (ou não há o que desenhar)?
   * Pintada, e não só baixada: senão a imagem nova tocaria com a chuva da hora
   * anterior. A reprodução pergunta pelo PRÓXIMO quadro, então a pintura sai
   * adiantada (o gate é consultado a cada passo).
   */
  pronto(t: number | null): boolean {
    if (!useGsmapStore.getState().visivel) return true;
    const q = this.quadroDe(t);
    if (!q) return true;
    const k = chaveDe(q);
    const e = this.cache.get(k);
    if (!e || e.estado === 'carregando') return false;
    if (e.estado === 'falhou' || this.pinturas.has(k)) return true;
    this.pintarAdiantado(k, e.bytes);
    return false;
  }

  /** Exportação: resolve quando a hora do quadro atual está na tela (ou não há o que pôr). */
  private quandoProntoAtual(): Promise<void> | void {
    if (this.resolvido()) return;
    return new Promise((resolve) => this.esperas.push(resolve));
  }

  /** O quadro exibido já mostra o que deve (a hora dele, ou nada)? */
  private resolvido(): boolean {
    if (!useGsmapStore.getState().visivel) return true;
    const q = this.quadroDe(shownTimeOf(useTimeStore.getState()));
    if (!q) return true;
    const k = chaveDe(q);
    return this.naTela === k || this.cache.get(k)?.estado === 'falhou';
  }

  /** Libera a exportação quando o quadro exibido ficou pronto — reavaliado a cada mudança (hora trocada, falha, desligar). */
  private soltarEsperas(): void {
    if (!this.esperas.length || !this.resolvido()) return;
    const lista = this.esperas;
    this.esperas = [];
    lista.forEach((r) => r());
  }

  /** Horas necessárias para o loop atual; baixa o que falta e descarta o que saiu. */
  private replanejar(): void {
    const { visivel, indice } = useGsmapStore.getState();
    this.layer.setVisible(visivel);
    if (indice !== this.indice) {
      this.indice = indice;
      this.quadros = indice?.quadros ?? [];
      this.meios = this.quadros.map((q) => q.t);
      this.tabela = indice ? tabelaDeValores(indice.codificacao) : null;
      // Grade nova (formato novo): bytes, geometria e pinturas não servem mais.
      if (indice && this.geo && !mesmaGrade(this.geo.grade, indice.grade)) {
        this.esvaziar();
        this.geo = null;
      }
    }
    if (!visivel) {
      this.fila = [];
      // Desligada, a memória volta: bytes, pinturas e o worker.
      this.esvaziar();
      this.pintor.encerrar();
      this.mostrar();
      this.atualizarProgresso(0);
      return;
    }
    const { frames, index } = useTimeStore.getState();
    const precisa = new Map<string, QuadroGsmap>();
    // Do quadro atual para trás, e depois o resto: o que está na tela chega primeiro.
    const todoLoop = loopLoadAllowed();
    const ordem = frames.map((_, k) => (index - k + frames.length) % frames.length);
    for (const i of ordem) {
      const q = this.quadroDe(frames[i]);
      if (!q) continue;
      const k = chaveDe(q);
      if (todoLoop || i === index || this.cache.get(k)?.estado === 'ok') precisa.set(k, q);
    }
    for (const [k, e] of this.cache) {
      if (precisa.has(k)) continue;
      if (e.estado === 'carregando') e.ctrl.abort();
      this.cache.delete(k);
    }
    for (const k of [...this.pinturas.keys()]) {
      // A da tela fica até a próxima entrar (a fonte ainda a desenha).
      if (!precisa.has(k) && k !== this.naTela) this.descartarPintura(k);
    }
    this.fila = [...precisa.entries()].filter(([k]) => !this.cache.has(k)).map(([, q]) => q);
    this.atualizarProgresso(precisa.size);
    this.bombear();
    this.mostrar();
  }

  /** Solta bytes e pinturas (e tira a imagem da tela). */
  private esvaziar(): void {
    this.geracao++;
    this.pedidos.clear();
    for (const e of this.cache.values()) if (e.estado === 'carregando') e.ctrl.abort();
    this.cache.clear();
    this.trocar(null);
    for (const b of this.pinturas.values()) b.close();
    this.pinturas.clear();
  }

  private atualizarProgresso(total?: number): void {
    let baixados = 0;
    let falhas = 0;
    for (const e of this.cache.values()) {
      if (e.estado === 'ok') baixados++;
      else if (e.estado === 'falhou') falhas++;
    }
    const antes = useGsmapStore.getState().progresso;
    const t = total ?? antes.total;
    if (antes.baixados !== baixados || antes.falhas !== falhas || antes.total !== t) {
      useGsmapStore.setState({ progresso: { baixados, total: t, falhas } });
    }
  }

  private bombear(): void {
    const grade = this.indice?.grade;
    if (!grade) return;
    while (this.emVoo < PARALELOS && this.fila.length) {
      const q = this.fila.shift()!;
      const k = chaveDe(q);
      if (this.cache.has(k)) continue;
      const ctrl = new AbortController();
      this.cache.set(k, { estado: 'carregando', ctrl });
      this.emVoo++;
      carregarQuadro(q, grade, ctrl.signal)
        .then((bytes) => {
          if (this.cache.get(k)?.estado !== 'carregando') return;
          this.cache.set(k, { estado: 'ok', bytes });
        })
        .catch((e: unknown) => {
          if (isAbortError(e) || this.cache.get(k)?.estado !== 'carregando') return;
          console.warn(`[AmaView] Chuva do GSMaP ${k} não carregou:`, e);
          this.cache.set(k, { estado: 'falhou' });
        })
        .finally(() => {
          this.emVoo--;
          this.atualizarProgresso();
          this.mostrar();
          this.bombear();
        });
    }
  }

  /** Recoloca na fila as horas que falharam (ex.: a conexão caiu por um minuto). */
  tentarDeNovo(): void {
    let algum = false;
    for (const [k, e] of this.cache) {
      if (e.estado === 'falhou') {
        this.cache.delete(k);
        algum = true;
      }
    }
    if (algum) this.replanejar();
  }

  /** Valor (mm/h) no lon/lat da hora na tela, NaN sem dado, ou null sem hora na tela. */
  valorNaTela(lon: number, lat: number): { v: number; q: QuadroGsmap } | null {
    if (!this.naTela || !this.indice || !this.tabela) return null;
    const q = this.quadros.find((x) => chaveDe(x) === this.naTela);
    const e = this.cache.get(this.naTela);
    if (!q || e?.estado !== 'ok') return null;
    // O mesmo valor que pinta a cor ali (bilinear, com a máscara): perto da
    // borda de uma mancha, o ponto da grade cru diria "0,1 mm/h" sobre um azul médio.
    const v = valorNoPonto(decodificar(e.bytes, this.tabela), this.indice.grade, lon, lat);
    return Number.isNaN(v) && !inside(this.indice.grade, lon, lat) ? null : { v, q };
  }

  /** Desenha a hora do instante exibido (a única que decide o que vai para a tela). */
  private mostrar(): void {
    if (this.disposed) return;
    const { visivel } = useGsmapStore.getState();
    const q = visivel ? this.quadroDe(shownTimeOf(useTimeStore.getState())) : null;
    let situacao: ChuvaNoQuadro;
    if (!q) {
      this.trocar(null);
      situacao = { estado: 'sem', c: null };
    } else {
      const k = chaveDe(q);
      const e = this.cache.get(k);
      if (e?.estado === 'ok') {
        if (this.pinturas.has(k)) {
          this.trocar(k);
          situacao = { estado: 'ok', c: q.c };
        } else {
          // Pintando: a anterior continua na tela até esta ficar pronta.
          this.pintarAdiantado(k, e.bytes, true);
          situacao = { estado: 'carregando', c: q.c };
        }
      } else if (e?.estado === 'falhou') {
        this.trocar(null);
        situacao = { estado: 'falhou', c: q.c };
      } else {
        // Ainda baixando: a anterior continua na tela até esta chegar.
        situacao = { estado: 'carregando', c: q.c };
        if (!e) {
          this.fila.unshift(q);
          this.bombear();
        }
      }
    }
    const antes = useGsmapStore.getState().noQuadro;
    if (antes.estado !== situacao.estado || antes.c !== situacao.c) useGsmapStore.setState({ noQuadro: situacao });
    this.soltarEsperas();
    if (visivel) this.adiantar();
  }

  /**
   * Pinta já as horas dos próximos quadros, no sentido do play: o gate só é
   * consultado quando a reprodução quer avançar, e pintar só aí parava o loop
   * a cada troca de hora (o tempo do pedido mais a espera da reprodução).
   */
  private adiantar(): void {
    const { frames, index } = useTimeStore.getState();
    if (frames.length < 2) return;
    for (let d = 1; d <= ADIANTAR_QUADROS; d++) {
      const q = this.quadroDe(frames[(index + d) % frames.length] ?? null);
      if (!q) continue;
      const k = chaveDe(q);
      const e = this.cache.get(k);
      if (e?.estado === 'ok') this.pintarAdiantado(k, e.bytes);
    }
  }

  private trocar(k: string | null): void {
    if (k === this.naTela) return;
    this.naTela = k;
    if (k === null) this.source.setFrameImage(null);
    else this.source.setFrameImage(this.pinturas.get(k)!, this.geometria().geo.extent);
  }

  /**
   * Pede a pintura da hora `k` (uma por hora; no máximo duas horas ao mesmo
   * tempo, a da tela sempre). O resultado só é guardado: quem põe na tela é
   * `mostrar()`, chamado em seguida.
   */
  private pintarAdiantado(k: string, bytes: Uint8Array, daTela = false): void {
    if (this.pinturas.has(k) || this.pedidos.has(k) || !this.tabela || !this.indice) return;
    if (!daTela && this.pedidos.size >= 2) return;
    this.pedidos.add(k);
    const geracao = this.geracao;
    const { grade, geo, chave } = this.geometria();
    const { extent: _extent, ...repro } = geo;
    this.pintor
      .pintar({ chave, grade, repro, fator: FATOR, valores: this.tabela, paleta: PALETA_GSMAP, bytes })
      .then((bmp) => {
        if (geracao !== this.geracao) {
          bmp.close();
          return;
        }
        this.pedidos.delete(k);
        // Desligada, desmontada ou hora que saiu do loop: não guarda.
        if (this.disposed || !useGsmapStore.getState().visivel || this.cache.get(k)?.estado !== 'ok' || this.pinturas.has(k)) {
          bmp.close();
          return;
        }
        this.guardar(k, bmp);
        this.mostrar();
      })
      .catch((e: unknown) => {
        if (geracao !== this.geracao) return;
        this.pedidos.delete(k);
        if (this.disposed || isAbortError(e)) return;
        console.warn(`[AmaView] Chuva do GSMaP ${k} não pintou:`, e);
        if (this.cache.get(k)?.estado === 'ok') this.cache.set(k, { estado: 'falhou' });
        this.mostrar();
      });
  }

  private guardar(k: string, bmp: ImageBitmap): void {
    this.pinturas.set(k, bmp);
    const atual = this.quadroDe(shownTimeOf(useTimeStore.getState()));
    const daTela = atual ? chaveDe(atual) : null;
    while (this.pinturas.size > MAX_PINTURAS) {
      // A mais antiga sai — nunca a que a fonte desenha, nem a do quadro exibido, nem a recém-pintada.
      const velha = [...this.pinturas.keys()].find((x) => x !== this.naTela && x !== daTela && x !== k);
      if (!velha) break;
      this.descartarPintura(velha);
    }
  }

  private descartarPintura(k: string): void {
    this.pinturas.get(k)?.close();
    this.pinturas.delete(k);
  }

  private geometria(): { grade: Grade; geo: GeometriaRadar; chave: string } {
    if (!this.geo) {
      const grade = this.indice!.grade;
      const px: [number, number] = [grade.nj * FATOR, grade.ni * FATOR];
      const geo = calcularGeometria(modeloLonLat(extensaoLonLat(grade)), px, lonLatToMap, mapToLonLat);
      this.geo = { grade, geo, chave: `${grade.lat0},${grade.lon0},${grade.passo},${grade.ni},${grade.nj}` };
    }
    return this.geo;
  }

  dispose(): void {
    this.disposed = true;
    this.paradas.forEach((p) => p());
    this.esvaziar();
    this.esperas.forEach((r) => r());
    this.esperas = [];
    this.pintor.encerrar();
    this.map.removeLayer(this.layer);
  }
}

function inside(g: Grade, lon: number, lat: number): boolean {
  const fi = (g.lat0 - lat) / g.passo;
  const fj = (lon - g.lon0) / g.passo;
  return fi >= -0.5 && fj >= -0.5 && fi <= g.ni - 0.5 && fj <= g.nj - 0.5;
}

function mesmaGrade(a: Grade, b: Grade): boolean {
  return a.lat0 === b.lat0 && a.lon0 === b.lon0 && a.passo === b.passo && a.ni === b.ni && a.nj === b.nj;
}
